import {db, uuid, ENV, getAssessmentDB, offlineSyncDB} from "../Database.svelte";
import {readable, get} from "svelte/store";

import {getJWT, session} from "../Utilities/Session.svelte";
import {getTimestamp} from '../Utilities/time_format';

import AssessmentTypeModel from './assessmentTypeModel';
import RecurringAssessmentMapping from "./recurringAssessmentMapping";
import QuestionTypes from "../lib/QuestionTypes";
import { doFetch } from "../lib/data";

export default class Assessment {
    constructor (assessment) {
        if (!assessment) return;

        this.assessment_id = assessment._id;

        if (assessment.assessment_db) {
            this.custom_db = true;
            this.db = getAssessmentDB(assessment.assessment_db);
        } else {
            this.db = db;
        }

        if (assessment._id) {
            this.store = this.getAssessmentStore();
        }
        this.session = get(session);

        this.domainCount = {}

        this.assessment_data = {
            model: this,
            details: assessment || {},
            assessment_type_data: {} || assessment.assessment_type_data,
            control_notes: {},
            previous_control_notes: {
                _id: `${this.assessment_id}:prev_notes`,
                notes: {}
            },
            assessor_results: {},
            assessor_tags: {},
            question_tag_values: {},
            results: {},
            evidence: {},
            scope_notes: [],
            report_notes: [],
            recommendations: {},
            key_recommendations: [],
            positive_controls: [],
            service_recommendations: [],
            scratch_notes: {},
            notepad: {},
            recommendation_map: {},
            policy_status: {},
            domains: {
                _id: `${this.assessment_id}:domains`,
                names: [],
                mapping: {}
            },
            question_tags: {
                _id: `${this.assessment_id}:question_tags`,
                tags: []
            },
            comment_tags: {
                _id: `${this.assessment_id}:comment_tags`,
                tags: []
            },
            report_data: {},
            library_tags: {},
            library_notes: {},
            slides: {},
            orphaned_controls: {
                _id: `${this.assessment_id}:orphaned_controls`,
                controls: []
            }
        }
    }

    getAssessmentStore () {
        if (!this.assessment_id) {
            throw new Error("Invalid Assessment");
        }


        return readable(undefined, (set) => {
            if (this.custom_db) {
                db.get(this.assessment_id).then((details) => {
                    this.assessment_data.details = details;
                });

                var detail_change_monitor = db.changes({
                    since: "now",
                    live: true,
                    include_docs: true,
                    doc_ids: [this.assessment_id]
                }).on("change", (change) => {
                    this.assessment_data.details = change.doc
                    set(this.assessment_data);
                })
            }

            this.db.allDocs({
                include_docs: true,
                startkey: this.assessment_id,
                endkey: `${this.assessment_id}\ufff0`
            }).then(result => {
                for (let row of result.rows) {
                    this.handleDoc(row);
                }
                set(this.assessment_data);
            });

            let changeMonitor = this.db.changes({
                since: 'now',
                live: true,
                include_docs: true,
                filter: "filters/starts_with",
                query_params: {
                    id: this.assessment_id
                }
            }).on("change", (change) => {
                this.handleDoc(change);

                set(this.assessment_data);
            });

            return () => {
                changeMonitor.cancel();

                if (detail_change_monitor) {
                    detail_change_monitor.cancel();
                }
            };
        })
    }

    static async getAssessmentDetails(assessment_id) {
        return db.get(assessment_id);
    }

    static async getAssessmentDataStatic(assessment_id) {

        if (!assessment_id) throw new Error("Assessment ID is required");

        // check if assessment has it's own Database
        let assessment_details = await Assessment.getAssessmentDetails(assessment_id);

        let assessment_db = db;
        if (assessment_details.assessment_db) {
            assessment_db = getAssessmentDB(assessment_details.assessment_db);
        }

        let handleDoc = (new Assessment()).handleDoc

        let data = {
            assessment_id,
            assessment_data: {
                details: assessment_details || {},
                assessment_type_data: {} || assessment_details.assessment_type_data,
                control_notes: {},
                assessor_results: {},
                assessor_tags: {},
                question_tag_values: {},
                results: {},
                evidence: {},
                scope_notes: [],
                report_notes: [],
                recommendations: {},
                key_recommendations: [],
                positive_controls: [],
                service_recommendations: [],
                scratch_notes: {},
                notepad: {},
                recommendation_map: {},
                policy_status: {},
                domains: {
                    _id: `${this.assessment_id}:domains`,
                    names: [],
                    mapping: {}
                },
                question_tags: {
                    _id: `${this.assessment_id}:question_tags`,
                    tags: []
                },
                comment_tags: {
                    _id: `${this.assessment_id}:comment_tags`,
                    tags: []
                },
                report_data: {},
                library_tags: {},
                library_notes: {},
                slides: {},
                orphaned_controls: {}
            }
        }

        await assessment_db.allDocs({
            include_docs: true,
            startkey: assessment_id,
            endkey: `${assessment_id}\ufff0`
        }).then((result) => {
            result.rows.forEach((row) => {
                handleDoc.apply(data, [row]);
            });
        });

        return data.assessment_data;
    }

    handleDoc(row) {
        let id, type, rest;
        let assessor, control_id, tag_path;
        let family_id;
        let section_id, page_id, note_id;
        let idx;

        if (row.id == this.assessment_id) {
            this.assessment_data.details = row.doc

            if (row.doc.assessment_type_data) {
                this.assessment_data.assessment_type_data = {
                    _id: `${this.assessment_id}:assessment_type_data`,
                    ...row.doc.assessment_type_data
                }
            }

            return;
        }

        [id, type, ...rest] = row.id.split(':');

        switch (type) {
            case "assessment_type_data":
                this.assessment_data.assessment_type_data = row.doc
                break
            case "note":
                control_id = rest[0];

                // create array if it doesn't exist
                if (!row.deleted && !(this.assessment_data.control_notes[control_id] && this.assessment_data.control_notes[control_id].length)) {
                    this.assessment_data.control_notes[control_id] = [row.doc]
                } else {
                    // get insertion location
                    let idx = this.assessment_data.control_notes[control_id].length - 1;

                    while (idx && row.doc._id < this.assessment_data.control_notes[control_id][idx]._id) {
                        idx--;
                    }

                    // check if note already exists
                    if (this.assessment_data.control_notes[control_id][idx]._id == row.id) {
                        // check if note is deleted
                        if (row.deleted){
                            // delete row
                            this.assessment_data.control_notes[control_id].splice(idx, 1)
                        } else {
                            // update note
                            this.assessment_data.control_notes[control_id].splice(idx, 1, row.doc)
                        }
                    } else {
                        this.assessment_data.control_notes[control_id].splice(idx+1, 0, row.doc)
                    }
                }
                break;

            case "answer":
                [assessor, control_id] = [...rest];

                if (!this.assessment_data.assessor_results[assessor]) {
                    this.assessment_data.assessor_results[assessor] = {}
                }

                this.assessment_data.assessor_results[assessor][control_id] = row.doc;

                break;

            case "result":
                control_id = rest[0];
                this.assessment_data.results[control_id] = row.doc;
                break;

            case "policy_status":
                let policy_id = rest[0];
                this.assessment_data.policy_status[policy_id] = row.doc
                break;
            case "evidence":
                control_id = rest[0];
                if (row.deleted) {
                    this.assessment_data.evidence[control_id] = this.assessment_data.evidence[control_id].filter((e) => e._id != row.id);
                } else {
                    if (!this.assessment_data.evidence[control_id]){
                        this.assessment_data.evidence[control_id] = [row.doc]
                    } else {
                        this.assessment_data.evidence[control_id].push(row.doc)
                    }
                }
                break

            case "scope_note":
                // get note insert location
                idx = this.assessment_data.scope_notes.length - 1;

                // handle first note
                if (idx == -1) {
                    this.assessment_data.scope_notes.push(row.doc);
                    break
                }

                while (idx && row.doc._id < this.assessment_data.scope_notes[idx]._id) {
                    idx--;
                }

                // check if note already exists
                if (this.assessment_data.scope_notes[idx]._id == row.id) {
                    //check if note is deleted
                    if (row.deleted) {
                        this.assessment_data.scope_notes.splice(idx, 1);
                    } else {
                        this.assessment_data.scope_notes.splice(idx, 1, row.doc);
                    }
                } else {
                    this.assessment_data.scope_notes.splice(idx+1, 0, row.doc)
                }
                break;

            case "report_note":
                // get note insert location
                idx = this.assessment_data.report_notes.length - 1;

                // handle first note
                if (idx == -1) {
                    this.assessment_data.report_notes.push(row.doc);
                    break
                }

                while (idx && row.doc._id < this.assessment_data.report_notes[idx]._id) {
                    idx--;
                }

                // check if note already exists
                if (this.assessment_data.report_notes[idx]._id == row.id) {
                    //check if note is deleted
                    if (row.deleted) {
                        this.assessment_data.report_notes.splice(idx, 1);
                    } else {
                        this.assessment_data.report_notes.splice(idx, 1, row.doc);
                    }
                } else {
                    this.assessment_data.report_notes.splice(idx+1, 0, row.doc)
                }
                break;

            case "scratch_note":
                assessor = rest[0];

                this.assessment_data.scratch_notes[assessor] = row.doc;
                break;

            case "notepad_section":
                section_id = rest[0]

                if (row.deleted) {
                    delete this.assessment_data.notepad[section_id];
                    break
                }

                if (!this.assessment_data.notepad[section_id]) {
                    this.assessment_data.notepad[section_id] = {
                        details: {},
                        pages: {}
                    }
                }

                this.assessment_data.notepad[section_id].details = row.doc;
                break
            case "notepad_page":
                [section_id, page_id] = rest;

                if (row.deleted) {
                    try {
                        delete this.assessment_data.notepad[section_id].pages[page_id]
                    } finally {
                        break
                    }
                }

                if (!this.assessment_data.notepad[section_id]) {
                    this.assessment_data.notepad[section_id] = {
                        details: {},
                        pages: {}
                    }
                }

                if (!this.assessment_data.notepad[section_id].pages[page_id]) {
                    this.assessment_data.notepad[section_id].pages[page_id] = {
                        details: {},
                        notes: []
                    }
                }

                this.assessment_data.notepad[section_id].pages[page_id].details = row.doc;

                break;
            case "notepad_note":
                [section_id, page_id, note_id] = rest;

                if (row.deleted) {
                    if (this.assessment_data.notepad[section_id] && this.assessment_data.notepad[section_id].pages[page_id]) {
                        this.assessment_data.notepad[section_id].pages[page_id].notes = this.assessment_data.notepad[section_id].pages[page_id].notes.filter((n) => n._id != row.id)
                    }
                    break;
                }

                if (!this.assessment_data.notepad[section_id]) {
                    this.assessment_data.notepad[section_id] = {
                        details: {
                            section_id: section_id,
                            name: 'Orphaned Section'
                        },
                        pages: {}
                    }
                }

                if (!this.assessment_data.notepad[section_id].pages[page_id]) {
                    this.assessment_data.notepad[section_id].pages[page_id] = {
                        details: {
                            page_id: page_id,
                            name: "Orphaned Page"
                        },
                        notes: [row.doc]
                    }
                    break;
                }

                let notes = this.assessment_data.notepad[section_id].pages[page_id].notes;

                if (notes.length == 0) {
                    notes = [row.doc];
                } else {
                    // get insertion location
                    let idx = notes.length - 1;

                    while (idx && row.doc._id < notes[idx]._id) {
                        idx--;
                    }

                    // check if note already exists
                    if (notes[idx]._id == row.id) {
                        // update note
                        notes.splice(idx, 1, row.doc)
                    } else {
                        notes.splice(idx+1, 0, row.doc)
                    }
                }

                this.assessment_data.notepad[section_id].pages[page_id].notes = notes;

                break;
            case "recommendation":
                family_id = rest[0];
                if (row.deleted) {
                    this.assessment_data.recommendations[family_id] = this.assessment_data.recommendations[family_id].filter(r => r._id != row.id);
                } else {
                    if (!this.assessment_data.recommendations[family_id]) {
                        this.assessment_data.recommendations[family_id] = [row.doc];
                    } else {
                        let found = false;

                        this.assessment_data.recommendations[family_id] = this.assessment_data.recommendations[family_id].map((r) => {
                            if (r._id != row.id) return r;
                            found = true;
                            return row.doc
                        })
                        if (!found) {
                            this.assessment_data.recommendations[family_id].push(row.doc);
                        }
                    }
                }
                break;
            case "positive_control":
                if (row.deleted) {
                    this.assessment_data.positive_controls = this.assessment_data.positive_controls.filter(control => control._id != row.id);
                } else {
                    let found = false;

                    this.assessment_data.positive_controls = this.assessment_data.positive_controls.map((control) => {
                        if (control._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return control
                        }
                    });

                    if (!found) {
                        this.assessment_data.positive_controls.push(row.doc);
                    }
                }
                break;
            case "service_recommendation":
                if (row.deleted) {
                    this.assessment_data.service_recommendations = this.assessment_data.service_recommendations.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_data.service_recommendations = this.assessment_data.service_recommendations.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_data.service_recommendations.push(row.doc);
                    }
                }
                break;
            case "key_recommendation":
                if (row.deleted) {
                    this.assessment_data.key_recommendations = this.assessment_data.key_recommendations.filter(control => control._id != row.id);
                } else {
                    let found = false;

                    this.assessment_data.key_recommendations = this.assessment_data.key_recommendations.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_data.key_recommendations.push(row.doc);
                    }
                }
                break;
            case "domains":
                this.assessment_data.domains = row.doc;
                break;

            case "question_tags":
                this.assessment_data.question_tags = row.doc;
                break

            case "question_tag":
                tag_path = rest[0];

                this.assessment_data.question_tag_values[tag_path] = row.doc;

                break;

            case "comment_tags":
                this.assessment_data.comment_tags = row.doc;
                break

            case "section_results":
                this.assessment_data.report_data[row.doc.section_name] = row.doc;
                break

            case "library_item":
                if (row.deleted) {
                    this.assessment_data.report_data[row.doc.section_name] = this.assessment_data.report_data[row.doc.section_name].filter(item => item._id != row.id)
                    break;
                }

                if (!this.assessment_data.report_data[row.doc.section_name]) {
                    this.assessment_data.report_data[row.doc.section_name] = [row.doc]
                } else {
                    let found = false;
                    this.assessment_data.report_data[row.doc.section_name] = this.assessment_data.report_data[row.doc.section_name].map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        }
                        return item
                    })
                    if (!found) {
                        this.assessment_data.report_data[row.doc.section_name].push(row.doc)
                    }
                }
                break

            case "library_tags":
                this.assessment_data.library_tags[row.doc.section_name] = row.doc;
                break

            case "library_note":
                let library_item_id = rest[0];

                // create array if it doesn't exist
                if (!row.deleted && !(this.assessment_data.library_notes[library_item_id] && this.assessment_data.library_notes[library_item_id].length)) {
                    this.assessment_data.library_notes[library_item_id] = [row.doc]
                } else {
                    // get insertion location
                    let idx = this.assessment_data.library_notes[library_item_id].length - 1;

                    while (idx && row.doc._id < this.assessment_data.library_notes[library_item_id][idx]._id) {
                        idx--;
                    }

                    // check if note already exists
                    if (this.assessment_data.library_notes[library_item_id][idx]._id == row.id) {
                        // check if note is deleted
                        if (row.deleted){
                            // delete row
                            this.assessment_data.library_notes[library_item_id].splice(idx, 1)
                        } else {
                            // update note
                            this.assessment_data.library_notes[library_item_id].splice(idx, 1, row.doc)
                        }
                    } else {
                        this.assessment_data.library_notes[library_item_id].splice(idx+1, 0, row.doc)
                    }
                }
                break;

            case "slide":
                if (row.deleted) {
                    this.assessment_data.slides[row.doc.slide_id] = undefined;
                } else {
                    this.assessment_data.slides[row.doc.slide_id] = row.doc;
                }
                break

            case "prev_notes":
                this.assessment_data.previous_control_notes = row.doc;
                break;

            case "orphaned_controls":
                this.assessment_data.orphaned_controls = row.doc;
                break;


            default:
                console.error("UNHANDLED ASSESSMENT DOC: ", row);
        }
    }

    static async createAssessment(assessment) {
        let assessment_type = (await AssessmentTypeModel.getAssessmentTypeStatic(assessment.type)).assessment_type_data;
        let assessment_type_data = await AssessmentTypeModel.flattenAssessmentType(assessment_type);

        let assessment_id = uuid();

        let jwt = await getJWT();

        try {
            let assessment_db = await doFetch("/assessment/create", {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                method: "PUT",
                body: JSON.stringify({
                    name: `${ENV}_${assessment_id}`,
                    users: assessment.users,
                    assessment_type_data,
                    assessment_id
                })
            });

            var assessment_db_name = await assessment_db.text();
        } catch (e) {
            throw new Error(`Error Creating Assessment: ${assessment_id}`);
        }

        let new_assessment = {
            _id: assessment_id,
            type: 'assessment',
            name: assessment.name,
            matter: assessment.matter,
            assessment_type_id: assessment.type,
            assessment_type: assessment_type_data.name,
            level: 5,
            create_date: +(new Date()),
            users: assessment.users,
            assessment_db: assessment_db_name
        }
        return db.put(new_assessment);
    }

    static async deleteAssessment(assessment) {
        let assessment_db_name = assessment.assessment_db;
        let jwt = await getJWT();

        if (assessment_db_name) {
            try {
                await doFetch(`/assessment/${assessment_db_name}`, {
                    headers: {
                        'Authorization': `Bearer ${jwt}`,
                        'Content-Type': 'application/json'
                    },
                    method: "DELETE"
                })
            } catch (e) {
                console.log(e);
                throw new Error(`Error Deleting Assessment: ${assessment_db_name}`)
            }
        }

        db.allDocs({
            include_docs: true,
            startkey: assessment._id,
            endkey: `${assessment._id}:\ufff0`
        }).then(result => {
            let docs_to_delete = [];
            for (let row of result.rows) {
                row.doc._deleted = true;
                docs_to_delete.push(row.doc);
            }
            db.bulkDocs(docs_to_delete);
        })
    }

    static startSync(assessment_details) {
        if (!assessment_details.assessment_db) {
            throw new error("Database is not syncable")
        }

        offlineSyncDB.put({
            _id: assessment_details.assessment_db
        });
    }

    static stopSync(assessment_details) {
        offlineSyncDB.get(assessment_details.assessment_db).then((doc) => {
            doc._deleted = true;
            offlineSyncDB.put(doc);
        })
    }

    static getAssessmentList() {
        let assessment_list = [];
        return readable(undefined, (set) => {
            db.find({
                selector: {
                    type: 'assessment'
                }
            }).then((results) => {
                assessment_list = results.docs;
                set(assessment_list);
            });

            let changeMonitor = db.changes({
                since: "now",
                live: true,
                include_docs: true,
                filter: "filters/doc_type",
                query_params: {
                    type: "assessment"
                }
            }).on("change", (change) => {
                if (change.deleted) {
                    assessment_list = assessment_list.filter((a) => a._id != change.id)
                } else {
                    let found = false;
                    assessment_list = assessment_list.map((a) => {
                        if (a._id != change.id) return a;
                        found = true;
                        return change.doc
                    })

                    if (!found) {
                        assessment_list.push(change.doc)
                    }
                }

                set(assessment_list);
            })

            return () => {
                changeMonitor.cancel();
            }
        })
    }

    static saveDetails(details) {
        return db.put(details);
    }

    saveAssessmentTypeData(assessment_type_data) {
        return this.db.put(assessment_type_data);
    }

    setLevel(level) {
        this.assessment_data.details.level = level;

        this.db.put(this.assessment_data.details);
    }

    getAnswer (assessor, control_id, domain) {
        let q_type = "practice_status" // backwards compatability from when we were tracking both practice_status and maturity
        return (this.assessment_data.assessor_results[assessor] && this.assessment_data.assessor_results[assessor] [`${control_id}.${domain}.${q_type}`]) || {
            _id: `${this.assessment_id}:answer:${assessor}:${control_id}.${domain}.${q_type}`,
            type: 'answer',
            value: undefined
        }
    }

    setAnswer (assessor, control_id, domain, q_type, new_answer) {
        // backwards compatability from when we were tracking both practice_status and maturity
        if (arguments.length == 4) {
            new_answer = q_type;
            q_type = "practice_status"
        }

        let answer = this.getAnswer(assessor, control_id, domain , q_type);

        if (new_answer == answer.value) {
            answer.value = undefined
        } else {
            answer.value = new_answer;
        }

        return this.db.put(answer);
    }

    getResult(control_id, domain) {
        let q_type = "practice_status"; // backwards compatability from when we were tracking both practice_status and maturity
        return (this.assessment_data.results[`${control_id}.${domain}.${q_type}`] || {
            _id: `${this.assessment_id}:result:${control_id}.${domain}.${q_type}`,
            type: 'result',
            value: undefined
        });
    }

    setResult(control_id, domain, q_type, new_result) {
        // backwards compatability from when we were tracking both practice_status and maturity
        if (arguments.length == 3) {
            new_result = q_type;
            q_type = "practice_status";
        }

        let result = this.getResult(control_id, domain, q_type);

        if (new_result == result.value) {
            result.value = undefined
        } else {
            result.value = new_result
        }

        return this.db.put(result);
    }

    addDomain(name) {
        this.assessment_data.domains.names.push(name);

        this.db.put(this.assessment_data.domains)
    }

    deleteDomain(name) {
        this.assessment_data.domains.names.splice(this.assessment_data.domains.names.indexOf(name),1);

        for (let control_id of Object.keys(this.assessment_data.domains.mapping)) {
            if (this.assessment_data.domains.mapping[control_id].indexOf(name) != -1) {
                this.assessment_data.domains.mapping[control_id].splice(this.assessment_data.domains.mapping[control_id].indexOf(name), 1);
                if (this.assessment_data.domains.mapping[control_id].length == 0) {
                    this.assessment_data.domains.mapping[control_id] = undefined;
                }
            }
        }

        this.db.put(this.assessment_data.domains);
    }

    getTag(control_id, tag_id) {
        return this.assessment_data.question_tag_values[`${control_id}.${tag_id}`] || {
            _id: `${this.assessment_id}:question_tag:${control_id}.${tag_id}`,
            value: undefined
        }
    }

    setTag(control_id, tag_id, value) {
        let tag = this.getTag(control_id, tag_id);

        if (tag.value == value) {
            tag.value = undefined
        } else {
            tag.value = 1
        }

        this.db.put(tag);
    }

    addQuestionTag(name, color) {
        this.assessment_data.question_tags.tags.push({
            id: uuid(),
            name: name,
            color_class: color
        })

        this.db.put(this.assessment_data.question_tags)
    }

    deleteQuestionTag(tag) {
        this.assessment_data.question_tags.tags = this.assessment_data.question_tags.tags.filter((t) => t.id != tag.id);

        this.db.put(this.assessment_data.question_tags);
    }

    addCommentTag(name, color) {
        this.assessment_data.comment_tags.tags.push({
            tag_id: uuid(),
            name: name,
            color_class: color
        })

        return this.db.put(this.assessment_data.comment_tags)
    }

    deleteCommentTag(tag) {
        this.assessment_data.comment_tags.tags = this.assessment_data.comment_tags.tags.filter((t) => t.tag_id != tag.tag_id);

        this.db.put(this.assessment_data.comment_tags);
    }

    doMap(control_id, domain) {
        if (!this.assessment_data.domains.mapping[control_id]) {
            this.assessment_data.domains.mapping[control_id] = [domain]
        } else {
            if (!this.assessment_data.domains.mapping[control_id].includes(domain)) {
                this.assessment_data.domains.mapping[control_id].push(domain);
            }
        }
    }

    bulkMap(controls, domains) {
        for (let control of controls) {
            for (let domain of domains) {
                this.doMap(control.id, domain);
            }
        }

        this.db.put(this.assessment_data.domains);
    }

    mapDomain(control_id, domain) {
        this.doMap(control_id, domain)

        this.db.put(this.assessment_data.domains)
    }

    unmapDomain(control_id, domain) {
        this.assessment_data.domains.mapping[control_id].splice(this.assessment_data.domains.mapping[control_id].indexOf(domain), 1);

        this.db.put(this.assessment_data.domains);
    }

    addScopeNote(text, color_class) {
        let note = {
            _id: `${this.assessment_id}:scope_note:${uuid()}`,
            type: "scope_note",
            user: this.session.user.email,
            timestamp: getTimestamp(),
            ts: +(new Date()),
            note: text,
            color_class: color_class
        }

        this.db.put(note);
    }

    updateScopeNote(note) {
        return this.db.put(note);
    }

    deleteScopeNote(note) {
        note._deleted = true;
        return this.db.put(note);
    }

    addReportNote(text, metadata, parent) {
        let note = {
            _id: `${this.assessment_id}:report_note:${uuid()}`,
            type: "report_note",
            user: this.session.user.email,
            timestamp: getTimestamp(),
            ts: +(new Date()),
            note: text,
            parent,
            metadata
        }

        this.db.put(note);
    }

    updateReportNote(note) {
        return this.db.put(note);
    }

    deleteReportNote(note) {
        note._deleted = true;
        return this.db.put(note);
    }

    savePreviousNotes(notes) {
        return this.db.put(notes);
    }

    addNote(control_id, text, tags) {
        let note = {
            _id: `${this.assessment_id}:note:${control_id}:${uuid()}`,
            type: 'note',
            user: this.session.user.email,
            timestamp: getTimestamp(),
            ts: +(new Date()),
            note: text,
            tags: tags
        }

        this.db.put(note);
    }

    updateNote(note) {
        this.db.put(note);
    }

    deleteNote(note) {
        note._deleted = true;
        this.db.put(note);
    }

    saveScratchNote(note) {
        if (!note._id) {
            note._id = `${this.assessment_id}:scratch_note:${this.session.user.email}`,
            note.type = 'scratch_note',
            note.user = this.session.user.email
        }

        this.db.put(note);
    }

    uploadEvidence(control_id, description, attachment) {
        let attachment_id = uuid();
        let evidence_info = {
            _id: `${this.assessment_id}:evidence:${control_id}:${attachment_id}`,
            type: "evidence",
            description: description
        };

        if (attachment) {
            evidence_info.attachment_id =  attachment_id;
            evidence_info.filename = attachment.name;
            evidence_info._attachments = {};

            evidence_info._attachments[attachment_id] = {
                content_type: attachment.type || 'application/octet-stream',
                data: attachment
            }
        }

        this.db.put(evidence_info);
    }

    getEvidence(attachment) {
        return this.db.getAttachment(attachment._id, attachment.attachment_id);
    }

    downloadEvidence(attachment) {
        this.getEvidence(attachment).then((blob) => {
            let url = URL.createObjectURL(blob);
            let link = document.createElement("a");
            link.href = url;
            link.download = attachment.filename;
            link.target = "_blank";

            link.click();

            URL.revokeObjectURL(url);
        })
    }

    deleteEvidence(attachment) {
        attachment._deleted = true;
        this.db.put(attachment);
    }

    addRecommendation(control_id, recommendation) {
        recommendation.recommendation_id = uuid();
        recommendation._id = `${this.assessment_id}:recommendation:${control_id}:${recommendation.recommendation_id}`
        recommendation.type = "recommendation"

        return this.db.put(recommendation)
    }

    updateRecommendation(recommendation) {
        return this.db.put(recommendation);
    }

    deleteRecommendation(recommendation) {
        recommendation._deleted = true;
        return this.db.put(recommendation);
    }

    addPositiveControl(control) {
        if (this.assessment_data.positive_controls.length >= 6) {
            return new Promise((resolve, reject) => {
                reject("Maximum of 6 Positive Controls Allowed");
            });
        }

        control.control_id = uuid();
        control._id = `${this.assessment_id}:positive_control:${control.control_id}`;
        control.type = "positive_control"

        return this.db.put(control);
    }

    updatePositiveControl(control) {
        return this.db.put(control);
    }

    deletePositiveControl(control) {
        control._deleted = true;
        return this.db.put(control);
    }

    addKeyRecommendation(recommendation) {
        recommendation.recommendation_id = uuid();
        recommendation._id = `${this.assessment_id}:key_recommendation:${recommendation.recommendation_id}`;
        recommendation.type = "key_recommendation";

        return this.db.put(recommendation);
    }

    updateKeyRecommendation(recommendation) {
        return this.db.put(recommendation);
    }

    deleteKeyRecommendation(recommendation) {
        recommendation._deleted = true;
        return this.db.put(recommendation);
    }

    addServiceRecommendation(recommendation) {
        recommendation.recommendation_id = uuid();
        recommendation._id = `${this.assessment_id}:service_recommendation:${recommendation.recommendation_id}`;
        recommendation.type = "service_recommendation"

        return this.db.put(recommendation);
    }

    updateServiceRecommendation(recommendation) {
        return this.db.put(recommendation);
    }

    deleteServiceRecommendation(recommendation) {
        recommendation._deleted = true;
        return this.db.put(recommendation);
    }

    getPolicyStatus(policy_id) {
        let policy = this.assessment_data.policy_status[policy_id];

        if (!policy) {
            let policy_data;
            for (let pol of this.assessment_data.details.assessment_type_data.policies) {
                if (pol.policy_id == policy_id) {
                    policy_data = pol;
                    break
                }
            }
            policy = {
                _id: `${this.assessment_id}:policy_status:${policy_id}`,
                name: policy_data.name,
                policy_id: policy_data.policy_id,
                rag_status: "Green",
                recommendation: policy_data.recommendation,
                references: policy_data.references
            }
        }

        return policy
    }

    setPolicyStatus(policy) {
        return this.db.put(policy)
    }

    addNotepadSection(section) {
        section.section_id = uuid();
        section._id = `${this.assessment_id}:notepad_section:${section.section_id}`;
        section.type = "notepad_section";

        return this.db.put(section);
    }

    saveNotepadSection(section) {
        return this.db.put(section);
    }

    deleteNotepadSection(section) {
        section._deleted = true;

        return this.db.put(section)
    }

    addNotepadPage(section, page) {
        page.page_id = uuid();
        page._id = `${this.assessment_id}:notepad_page:${section.section_id}:${page.page_id}`;
        page.type = "notepad_page";

        return this.db.put(page)
    }

    saveNotepadPage(page) {
        return this.db.put(page);
    }

    deleteNotepadPage(page) {
        page._deleted = true;

        return this.db.put(page)
    }

    addNotepadNote(section, page, text, tags) {
        let note_id = uuid();

        let note = {
            _id: `${this.assessment_id}:notepad_note:${section.section_id}:${page.page_id}:${note_id}`,
            note_id: note_id,
            type: 'notepad_note',
            user: this.session.user.email,
            timestamp: getTimestamp(),
            ts: +(new Date()),
            note: text,
            tags: tags
        }

        return this.db.put(note)
    }

    saveNotepadNote(note) {
        return this.db.put(note);
    }

    deleteNotepadNote(note) {
        note._deleted = true;
        return this.db.put(note);
    }

    saveSectionResults(section, details) {
        if (!details._id) {
            details._id = `${this.assessment_id}:section_results:${section.section_id}:${uuid()}`;
            details.type = "section_results"
            details.section_name = section.section_name;
        }

        return this.db.put(details);
    }

    saveLibraryItem(section, item) {
        if (!item._id) {
            item.item_id = uuid()
            item._id = `${this.assessment_id}:library_item:${section.section_id}:${item.item_id}`;
            item.type = "library_item"
            item.section_name = section.section_name
        }

        return this.db.put(item);
    }

    deleteLibraryItem(item) {
        if (item.type != "library_item") throw new Error("Invalid document type")
        item._deleted = true;
        return this.db.put(item);
    }

    saveSectionTags(section, details) {
        if (!details._id) {
            details._id = `${this.assessment_id}:library_tags:${section.section_id}:${uuid()}`;
            details.type = "library_tags"
            details.section_name = section.section_name;
        }

        return this.db.put(details);
    }

    saveSlide(slide) {
        if (!slide._id) {
            slide.type = "slide"
            slide._id = `${this.assessment_id}:slide:${slide.slide_id}`;
        }

        return this.db.put(slide);
    }

    deleteSlide(slide) {
        slide._deleted = true;
        return this.db.put(slide);
    }

    async loadPreviousResults(name, data, control_mapping_id) {
        let prev_control_mapping = {};
        if (control_mapping_id) {
            let control_mapping_data = await RecurringAssessmentMapping.getStatic(control_mapping_id);

            for (let mapping of control_mapping_data) {
                prev_control_mapping[mapping.details.from] = mapping.details.to;
            }
        }

        // build control map
        let control_map = {}
        for (let category of this.assessment_data.assessment_type_data.categories) {
            for (let subcategory of category.subcategories) {
                for (let control of subcategory.controls) {
                    control_map[control.id] = control;
                }
            }
        }

        // Question Type translation
        const ANSWERS = {
            "Yes": "Full",
            "Full": "Yes",

            "No": "None",
            "None": "No",

            "Partial": "Partial",

            "N/A": "N/A"
        }

        // container for previous notes
        let prev_notes = {};
        let orphaned_controls = [];

        // get controls
        for (let category of data.assessment_type_data.categories) {
            for (let subcategory of category.subcategories) {
                for (let control of subcategory.controls) {

                    // get result
                    let result = data.results[`${control.id}..practice_status`];
                    if (result != undefined && result.value) {
                        if (control.id in prev_control_mapping) {
                            let control_id = prev_control_mapping[control.id];

                            let new_control = control_map[control_id];

                            if (new_control.question_type !== control.question_type && new_control.question_type !== "free_text" && control.question_type !== "free_text") {
                                // translate answers
                                const buttons = QuestionTypes[control.question_type].buttons;
                                const current_answer = buttons[result.value].label;

                                for (let button of Object.values(QuestionTypes[new_control.question_type].buttons)) {
                                    if (button.label == current_answer) {
                                        this.setAnswer(name, control_id, '', button.value)
                                        break
                                    }
                                }
                            } else {
                                this.setAnswer(name, control_id, '', result.value);
                            }

                        } else if (control.id in control_map) {
                            this.setAnswer(name, control.id, '', result.value)
                        } else {
                            orphaned_controls.push({
                                control,
                                value: result.value
                            });
                        }
                    }

                    // get notes
                    let prev_control_notes = data.control_notes[control.id];
                    if (prev_control_notes != undefined) {
                        let cleaned_notes = prev_control_notes.map((d) => {
                            delete d._rev;
                            return d;
                        })

                        let control_id;
                        if (control.id in prev_control_mapping) {
                            control_id = prev_control_mapping[control.id]
                        } else {
                            control_id = control.id;
                        }

                        prev_notes[control_id] = cleaned_notes;
                    }
                }
            }
        }

        this.assessment_data.orphaned_controls.controls = orphaned_controls
        await this.saveOrphanedControls(this.assessment_data.orphaned_controls);

        this.assessment_data.previous_control_notes.notes = prev_notes;
        await this.savePreviousNotes(this.assessment_data.previous_control_notes);
    }

    saveOrphanedControls(orphaned_controls) {
        return this.db.put(orphaned_controls);
    }

    async addUser(email) {
        const details = JSON.parse(JSON.stringify(this.assessment_data.details))
        console.log(details);

        // check if user is already in the list
        if (details.users.includes(email)) {
            return;
        }

        // add user
        details.users.push(email);

        let jwt = await getJWT();


        await doFetch(`/assessment/${details.assessment_db}`, {
            headers: {
                'Authorization': `Bearer ${jwt}`,
                'Content-Type': 'application/json'
            },
            method: "POST",
            body: JSON.stringify(details)
        })


        return Assessment.saveDetails(details);
    }

    addLibraryNote(library_item_id, text, tags) {
        let note = {
            _id: `${this.assessment_id}:library_note:${library_item_id}:${uuid()}`,
            type: 'note',
            user: this.session.user.email,
            timestamp: getTimestamp(),
            ts: +(new Date()),
            note: text,
            tags: tags
        }

        this.db.put(note);
    }

    updateLibraryNote(note) {
        this.db.put(note);
    }

    deleteLibraryNote(note) {
        note._deleted = true;
        this.db.put(note);
    }
}